import React, { useState, useEffect, useCallback } from 'react';
import DashboardNav from '../components/dashboard/DashboardNav';
import DashboardWelcome from '../components/dashboard/Welcome';
import LeadsOverview from './LeadsOverview';
import Leads from './Leads';
import CustomerDetail from './CustomerDetail';
import OrganizationManagement from './organization/OrganizationManagement';
import OrganizationMembersManagement from './organization/OrganizationMembersManagement';
import TeamLeads from './organization/TeamLeads'; // Import für Team-Leads
import OrganizationLeads from './organization/OrganizationLeads'; // Import für Organisations-Leads
import TeamDashboard from '../components/dashboard/TeamDashboard'; // Neue Komponente für Team Dashboard
import OrganizationDashboard from '../components/dashboard/OrganizationDashboard';
import Stellungnahmen from './Stellungnahmen';
import Termine from './Termine';
import CallcenterDashboard from '../components/dashboard/CallcenterDashboard';

function Dashboard({ user, onLogout }) {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [showOrgManagement, setShowOrgManagement] = useState(false);
  const [showMembersManagement, setShowMembersManagement] = useState(false);
  
  // Neuer State für Filter
  const [currentFilter, setCurrentFilter] = useState(null);
  
  // Neue Zustände für Team-Leads und Organisations-Leads
  const [showTeamLeads, setShowTeamLeads] = useState(false);
  const [teamLeadsInfo, setTeamLeadsInfo] = useState({
    id: null,
    name: ''
  });
  
  const [showOrganizationLeads, setShowOrganizationLeads] = useState(false);
  const [organizationLeadsInfo, setOrganizationLeadsInfo] = useState({
    id: null,
    name: ''
  });

  // Neuer State für Team Dashboard Navigation vom OrganizationDashboard
  const [showTeamDashboardFromOrg, setShowTeamDashboardFromOrg] = useState(false);
  const [teamDashboardInfo, setTeamDashboardInfo] = useState({
    id: null,
    name: ''
  });

  const isCallcenterLeader = user?.isCallcenterLeader || false;
  const isCallcenterMitarbeiter = user?.isCallcenterMitarbeiter || false;

  // Erweiterte handleSectionChange Funktion
  const handleSectionChange = (section, options = {}) => {
    setActiveSection(section);
    setSelectedCustomerId(null);
    setShowOrgManagement(false);
    setShowMembersManagement(false);
    setShowTeamLeads(false);
    setShowOrganizationLeads(false);
    setShowTeamDashboardFromOrg(false); // Neue Zeile hinzugefügt
    
    // Filter setzen wenn vorhanden
    if (options.filter) {
      setCurrentFilter(options.filter);
    } else {
      setCurrentFilter(null);
    }
  };

  // Rest der Funktionen bleibt gleich...
  const handleSelectCustomer = (customerId) => {
    setSelectedCustomerId(customerId);
    setActiveSection('customer-details');
  };

  const handleBackToLeads = () => {
    setSelectedCustomerId(null);
    setActiveSection('leads');
  };

  // Funktionen für die Organisation-Verwaltung
  const handleOpenOrganizationManagement = useCallback(() => {
    setShowOrgManagement(true);
    setShowMembersManagement(false);
    setShowTeamLeads(false);
    setShowOrganizationLeads(false);
    setShowTeamDashboardFromOrg(false); // Neue Zeile hinzugefügt
  }, []);

  const handleCloseOrganizationManagement = () => {
    setShowOrgManagement(false);
    setActiveSection('dashboard');
  };

  // Funktionen für die Mitglieder-Verwaltung
  const handleOpenMembersManagement = useCallback(() => {
    setShowMembersManagement(true);
    setShowOrgManagement(false);
    setShowTeamLeads(false);
    setShowOrganizationLeads(false);
    setShowTeamDashboardFromOrg(false); // Neue Zeile hinzugefügt
  }, []);

  const handleCloseMembersManagement = () => {
    setShowMembersManagement(false);
    setActiveSection('dashboard');
  };
  
  // Neue Funktionen für Team-Leads
  const handleOpenTeamLeads = useCallback((teamId, teamName) => {
    
    setTeamLeadsInfo({
      id: teamId,
      name: teamName
    });
    
    setShowTeamLeads(true);
    setShowOrgManagement(false);
    setShowMembersManagement(false);
    setShowOrganizationLeads(false);
    setShowTeamDashboardFromOrg(false); // Neue Zeile hinzugefügt

  }, []);

  const handleCloseTeamLeads = () => {
    setShowTeamLeads(false);
    setTeamLeadsInfo({
      id: null,
      name: ''
    });
    setShowOrgManagement(true); // Zurück zur Org-Management Ansicht
  };
  
  // Neue Funktionen für Organisations-Leads als useCallback definieren
  const handleOpenOrganizationLeads = useCallback((organizationId) => {
    if (!organizationId) {
      console.error("Keine Organizations-ID übergeben!");
      return;
    }
    
    let orgName = "Organisation";
    if (user && user.organization_id === organizationId) {
      orgName = "Meine Organisation";
    }
    
    setOrganizationLeadsInfo({
      id: organizationId,
      name: orgName
    });
    setShowOrganizationLeads(true);
    setShowOrgManagement(false);
    setShowMembersManagement(false);
    setShowTeamLeads(false);
    setShowTeamDashboardFromOrg(false); // Neue Zeile hinzugefügt
  }, [user]);
  
  const handleCloseOrganizationLeads = () => {
    setShowOrganizationLeads(false);
    setOrganizationLeadsInfo({
      id: null,
      name: ''
    });
    setActiveSection('dashboard');
  };

  // NEUE FUNKTION: Navigation zum Team Dashboard vom Organisation Dashboard
  const handleNavigateToTeamDashboard = useCallback((teamId, teamName) => {
    
    setTeamDashboardInfo({
      id: teamId,
      name: teamName
    });
    
    setShowTeamDashboardFromOrg(true);
    setShowOrgManagement(false);
    setShowMembersManagement(false);
    setShowTeamLeads(false);
    setShowOrganizationLeads(false);
    setActiveSection('org-dashboard'); // Bleibe in der org-dashboard Section
  }, []);

  // NEUE FUNKTION: Zurück zum Organisations Dashboard
  const handleBackToOrganizationDashboard = () => {
    setShowTeamDashboardFromOrg(false);
    setTeamDashboardInfo({
      id: null,
      name: ''
    });
    // Bleibe in der org-dashboard Section, zeige aber wieder das normale OrganizationDashboard
  };

  // Rendere je nach Zustand verschiedene Komponenten
  const renderContent = () => {
    
    // Priorisierung der Ansichten
    if (showTeamLeads) {

      return (
        <TeamLeads
          user={user}
          teamId={teamLeadsInfo.id}
          teamName={teamLeadsInfo.name}
          onBack={handleCloseTeamLeads}
        />
      );
    }
    
    if (showOrganizationLeads) {
      return (
        <OrganizationLeads
          user={user}
          organizationId={organizationLeadsInfo.id}
          organizationName={organizationLeadsInfo.name}
          onBack={handleCloseOrganizationLeads}
        />
      );
    }
    
    if (showMembersManagement) {
      return (
        <OrganizationMembersManagement 
          user={user} 
          onBack={handleCloseMembersManagement} 
        />
      );
    }
    
    if (showOrgManagement) {
      return (
        <OrganizationManagement 
          user={user} 
          onBack={handleCloseOrganizationManagement} 
          onViewTeamLeads={handleOpenTeamLeads}
        />
      );
    }

    // NEUE BEDINGUNG: Team Dashboard vom Organisation Dashboard
    if (showTeamDashboardFromOrg && activeSection === 'org-dashboard') {
      return (
        <div className="team-dashboard-from-org">
          <div className="team-dashboard-header">
            <button className="back-button" onClick={handleBackToOrganizationDashboard}>
              ← Zurück zum Organisations Dashboard!
            </button>
            <h2>Team Dashboard: {teamDashboardInfo.name}</h2>
          </div>
          <TeamDashboard 
            user={{
              ...user,
              // Simuliere einen Teamleiter für dieses spezifische Team
              isTeamlead: true,
              team: {
                id: teamDashboardInfo.id,
                name: teamDashboardInfo.name
              }
            }}
          />
        </div>
      );
    }

    if (activeSection === 'customer-details' && selectedCustomerId) {
      return (
        <CustomerDetail
          user={user}
          customerId={selectedCustomerId}
          onBack={handleBackToLeads}
        />
      );
    }

    switch (activeSection) {
      case 'team-dashboard':
        // Normale Section für Team Dashboard (für echte Teamleiter)
        return (
          <TeamDashboard 
            user={user}
          />
        );
      case 'callcenter-dashboard':
        // Callcenter Dashboard für Callcenter-Leader
        return (
          <CallcenterDashboard user={user} />
        );
      case 'leads':
        return (
          <Leads 
            user={user} 
            currentFilter={currentFilter} // Filter an Leads-Komponente weitergeben
          />
        );
      case 'leads-overview':
        return (
          <LeadsOverview 
            user={user} 
            onSelectCustomer={handleSelectCustomer} 
          />
        );
      case 'termine':
        return (
          <Termine 
            user={user}
          />
        );
      case 'org-dashboard':
        // GEÄNDERT: Nur das normale OrganizationDashboard anzeigen wenn nicht showTeamDashboardFromOrg
        if (!showTeamDashboardFromOrg) {
          return (
            <OrganizationDashboard 
              user={user}
              onNavigateToTeamDashboard={handleNavigateToTeamDashboard}
            />
          );
        }
        break;
      
      case 'dashboard':
      default:
        return <DashboardWelcome user={user} />;
    }
  };

  return (
    <div className="dashboard-container">
      <DashboardNav
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        onLogout={onLogout}
        user={user}
        onOpenOrganizationManagement={handleOpenOrganizationManagement}
        onOpenMembersManagement={handleOpenMembersManagement}
        onViewOrganizationLeads={handleOpenOrganizationLeads}
        onViewTeamLeads={handleOpenTeamLeads}
        showOrgManagement={showOrgManagement}
        showMembersManagement={showMembersManagement}
        showOrganizationLeads={showOrganizationLeads}
      />
      
      <div className="dashboard-content">
        {renderContent()}
      </div>
    </div>
  );
}

export default Dashboard;